// w17 Olovgul — preview thumbnail. The full design at
// /templates/olovgul.html ships in autumn-warmth tones (terracotta +
// burnt orange + cream + chestnut, Cormorant Garamond italic). This 5:7
// thumbnail mirrors that palette so the gallery card reads like a
// vintage wedding album with a dried-flower wreath behind the names.

import type { LayoutCtx } from "./_shared";

export function OlovgulPreviewLayout({ names, date, venue, accent, ink, bg, font }: LayoutCtx) {
  const [a, b] = names.split(/\s*&\s*/);
  // Locked palette tokens for the wreath / vignette regardless of the
  // gallery-driven `accent` so the autumn identity remains consistent.
  const terracotta = "#B65B3A";
  const rust = "#8C3B22";
  const gold = "#C68B58";

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: bg,
        color: ink,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        padding: "10% 8%",
      }}
    >
      {/* Sepia inner vignette — the visual signature */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          background:
            "radial-gradient(ellipse at center, transparent 38%, rgba(58,31,18,0.10) 70%, rgba(58,31,18,0.22) 100%)",
          pointerEvents: "none",
        }}
      />
      {/* Hairline terracotta frame */}
      <div
        aria-hidden
        style={{ position: "absolute", inset: "5%", border: `1px solid ${terracotta}55`, pointerEvents: "none" }}
      />
      <div
        aria-hidden
        style={{ position: "absolute", inset: "6.5%", border: `0.5px solid ${gold}33`, pointerEvents: "none" }}
      />

      {/* Dried-pampas + autumn-leaf wreath behind names */}
      <svg
        aria-hidden
        viewBox="0 0 200 200"
        style={{
          position: "absolute",
          width: "70%",
          aspectRatio: "1 / 1",
          opacity: 0.5,
          pointerEvents: "none",
        }}
      >
        {/* Outer ring of pampas plumes */}
        <g stroke={rust} strokeWidth="0.6" fill="none" opacity="0.85">
          <path d="M100 22 Q 96 40 100 56" />
          <path d="M100 22 Q 104 40 100 56" />
          <path d="M100 22 Q 92 38 96 54" />
          <path d="M100 22 Q 108 38 104 54" />
        </g>
        {/* Side leaves left */}
        <g fill={terracotta} opacity="0.6">
          <ellipse cx="34" cy="100" rx="14" ry="5" transform="rotate(-20 34 100)" />
          <ellipse cx="44" cy="74" rx="11" ry="4" transform="rotate(-50 44 74)" />
          <ellipse cx="44" cy="126" rx="11" ry="4" transform="rotate(50 44 126)" />
        </g>
        {/* Side leaves right */}
        <g fill={gold} opacity="0.65">
          <ellipse cx="166" cy="100" rx="14" ry="5" transform="rotate(20 166 100)" />
          <ellipse cx="156" cy="74" rx="11" ry="4" transform="rotate(50 156 74)" />
          <ellipse cx="156" cy="126" rx="11" ry="4" transform="rotate(-50 156 126)" />
        </g>
        {/* Bottom leaves */}
        <g fill={rust} opacity="0.55">
          <ellipse cx="86" cy="178" rx="10" ry="3.5" transform="rotate(-18 86 178)" />
          <ellipse cx="114" cy="178" rx="10" ry="3.5" transform="rotate(18 114 178)" />
        </g>
        {/* Tiny berries dotted around */}
        <g fill={terracotta}>
          <circle cx="58" cy="60" r="1.6" />
          <circle cx="142" cy="60" r="1.6" />
          <circle cx="58" cy="140" r="1.6" />
          <circle cx="142" cy="140" r="1.6" />
        </g>
      </svg>

      {/* Eyebrow */}
      <div
        style={{
          fontFamily: "var(--font-ui)",
          fontSize: 7,
          fontWeight: 700,
          letterSpacing: "0.42em",
          color: rust,
          marginBottom: 14,
          display: "flex",
          alignItems: "center",
          gap: 8,
          position: "relative",
        }}
      >
        <span style={{ width: 14, height: 1, background: terracotta }} />
        TAKLIFNOMA
        <span style={{ width: 14, height: 1, background: terracotta }} />
      </div>

      {/* Names — split across two italic lines, vintage album feel */}
      <div
        style={{
          fontFamily: font,
          fontStyle: "italic",
          fontWeight: 400,
          fontSize: 32,
          lineHeight: 1.0,
          color: ink,
          letterSpacing: "0.01em",
          position: "relative",
        }}
      >
        {a ?? names}
      </div>
      <div
        style={{
          fontFamily: "'Pinyon Script', cursive",
          fontSize: 28,
          color: terracotta,
          margin: "6px 0",
          lineHeight: 1,
          position: "relative",
        }}
      >
        &
      </div>
      <div
        style={{
          fontFamily: font,
          fontStyle: "italic",
          fontWeight: 400,
          fontSize: 32,
          lineHeight: 1.0,
          color: ink,
          position: "relative",
        }}
      >
        {b ?? ""}
      </div>

      {/* Hairline divider */}
      <div
        aria-hidden
        style={{
          width: "44%",
          height: 1,
          background: terracotta,
          opacity: 0.55,
          margin: "20px 0 14px",
          position: "relative",
        }}
      />

      {/* Date · venue */}
      <div
        style={{
          fontFamily: "var(--font-ui)",
          fontSize: 7.5,
          fontWeight: 600,
          letterSpacing: "0.34em",
          color: ink,
          fontFeatureSettings: '"tnum" 1, "lnum" 1',
          position: "relative",
        }}
      >
        {date.replace(/\./g, " · ")}
      </div>
      <div
        style={{
          fontFamily: font,
          fontStyle: "italic",
          fontSize: 12,
          color: ink,
          opacity: 0.78,
          marginTop: 6,
          position: "relative",
        }}
      >
        {venue}
      </div>
    </div>
  );
}
